import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useAuth0 } from '@auth0/auth0-react'
import { apiClient } from '@/api/client'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { User, AlertCircle } from 'lucide-react'

interface NicknameModalProps {
  onComplete: () => void
  currentNickname?: string
}

export function NicknameModal({ onComplete, currentNickname }: NicknameModalProps) {
  const { getAccessTokenSilently } = useAuth0()
  const [nickname, setNickname] = useState(currentNickname || '')
  const [error, setError] = useState('')

  const updateNickname = useMutation({
    mutationFn: async (newNickname: string) => {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          scope: 'openid profile email',
        },
      })
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`
      const response = await apiClient.patch('/auth/profile', {
        nickname: newNickname,
        has_completed_onboarding: true,
      })
      return response.data
    },
    onSuccess: () => {
      onComplete()
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to save nickname')
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    // Validate nickname
    if (!nickname.trim()) {
      setError('Nickname is required')
      return
    }

    if (nickname.trim().length < 2) {
      setError('Nickname must be at least 2 characters')
      return
    }

    if (nickname.trim().length > 50) {
      setError('Nickname must be less than 50 characters')
      return
    }

    // Only allow letters, numbers, spaces, underscores, and hyphens
    if (!/^[a-zA-Z0-9\s_-]+$/.test(nickname.trim())) {
      setError('Nickname can only contain letters, numbers, spaces, underscores, and hyphens')
      return
    }

    updateNickname.mutate(nickname.trim())
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
        <div className="text-center mb-6">
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-blue-100 mb-4">
            <User className="h-6 w-6 text-blue-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {currentNickname ? 'Update Your Nickname' : 'Welcome! Choose Your Nickname'}
          </h2>
          <p className="text-sm text-gray-600">
            {currentNickname
              ? 'You can change your nickname anytime'
              : 'This is how other players will see you'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="nickname" className="block text-sm font-medium text-gray-700 mb-1">
              Nickname
            </label>
            <input
              type="text"
              id="nickname"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              placeholder="Enter your nickname"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              disabled={updateNickname.isPending}
              autoFocus
            />
            <p className="mt-1 text-xs text-gray-500">
              2-50 characters. Letters, numbers, spaces, underscores, and hyphens only.
            </p>
          </div>

          {error && (
            <div className="flex items-start space-x-2 p-3 bg-red-50 border border-red-200 rounded-md">
              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={updateNickname.isPending || !nickname.trim()}
            className="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {updateNickname.isPending ? (
              <>
                <LoadingSpinner size="sm" className="mr-2" />
                Saving...
              </>
            ) : (
              currentNickname ? 'Update Nickname' : 'Get Started'
            )}
          </button>
        </form>
      </div>
    </div>
  )
}

